import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { GlobalSet } from "../../global/GlobalProvider";

const ENDPOINT = "https://backoffice.rmcrownmarket.com/api/apps-arb-trade";
const REFRESH_MS = 2000;

export default function ArbTrade() {
  const { BaseUrl } = GlobalSet();
  const [rows, setRows] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(false);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false);

  useEffect(() => {
    let disposed = false;
    let timer;
    let controller;

    const fetchTrades = async () => {
      if (disposed || pausedRef.current || document.hidden) {
        timer = setTimeout(fetchTrades, REFRESH_MS);
        return;
      }
      controller = new AbortController();
      try {
        const response = await axios.get(ENDPOINT, { signal: controller.signal });
        if (disposed) return;
        if (response.data?.status === "success" && Array.isArray(response.data?.data)) {
          setRows(response.data.data);
          setLastUpdated(new Date());
          setError(false);
        } else {
          setError(true);
        }
      } catch (err) {
        if (!disposed && err?.code !== "ERR_CANCELED") setError(true);
      } finally {
        if (!disposed) timer = setTimeout(fetchTrades, REFRESH_MS);
      }
    };
    fetchTrades();
    return () => {
      disposed = true;
      clearTimeout(timer);
      controller?.abort();
    };
  }, []);

  const togglePause = () => {
    pausedRef.current = !pausedRef.current;
    setPaused(pausedRef.current);
  };

  const assetUrl = (path) => {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    return `${String(BaseUrl || "").replace(/\/$/, "")}/${String(path).replace(/^\//, "")}`;
  };

  return (
    <>
      <style>{`
        html, body, #root { background:#00191D !important; }
        .arb2, .arb2 * { box-sizing:border-box; }
        .arb2 { --navy:#00191D;--cyan:#16B8C7;--line:rgba(22,184,199,.23);--muted:#9bb7ba;min-height:100vh;background:#00191D;color:#fff;padding:18px 4px 32px;font-size:14px; }
        .arb2-inner { width:100%;max-width:1360px;margin:auto; }
        .arb2-top { display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap;margin-bottom:14px;padding:20px;border:1px solid var(--line);border-radius:18px;background:linear-gradient(110deg,#05343b,#00262c 55%,#00191D); }
        .arb2-eyebrow { color:#69dce3;font-size:11px;font-weight:800;letter-spacing:1.7px;text-transform:uppercase;margin:0 0 6px; }
        .arb2 h1 { margin:0;font-size:clamp(19px,2.4vw,27px);line-height:1.2;font-weight:800;color:#fff; }
        .arb2-sub { color:var(--muted);margin:8px 0 0;font-size:13px; }
        .arb2-actions { display:flex;align-items:center;gap:9px;flex-wrap:wrap; }
        .arb2-live { display:inline-flex;align-items:center;gap:7px;padding:10px 12px;border:1px solid var(--line);border-radius:11px;background:#07333a;color:#a4f4f2;font-size:12px;font-weight:700; }
        .arb2-dot { width:7px;height:7px;background:#33d9a6;border-radius:50%;box-shadow:0 0 0 3px rgba(51,217,166,.12); }
        .arb2-dot.off { background:#ffb85a;box-shadow:none; }
        .arb2-btn { border:0;border-radius:11px;background:linear-gradient(120deg,#16B8C7,#68e4e5);color:#00252a;font-weight:800;padding:11px 15px;cursor:pointer;transition:transform .18s,filter .18s; }
        .arb2-btn:hover { transform:translateY(-2px);filter:brightness(1.08); }
        .arb2-metrics { display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-bottom:14px; }
        .arb2-metric { padding:17px;border-radius:16px;background:#fff;color:#0c2e34;border:1px solid #d8eaeb; }
        .arb2-metric:nth-child(2) { background:#063039;color:#fff;border-color:var(--line); }
        .arb2-metric:nth-child(2) .arb2-metric-label { color:#b0d1d4; }
        .arb2-metric-label { font-size:12px;color:#5e777c;margin:0 0 9px;font-weight:650; }
        .arb2-metric-value { font-size:clamp(17px,2vw,24px);font-weight:850;margin:0;overflow-wrap:anywhere; }
        .arb2-panel { overflow:hidden;border:1px solid var(--line);border-radius:18px;background:#05272d; }
        .arb2-panelhead { padding:16px 18px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;border-bottom:1px solid var(--line); }
        .arb2-panelhead h2 { font-size:16px;color:#fff;margin:0;font-weight:800; }
        .arb2-panelhead p { color:var(--muted);font-size:12px;margin:5px 0 0; }
        .arb2-count { padding:7px 10px;background:#fff;border-radius:9px;color:#00414a;font-size:12px;font-weight:800; }
        .arb2-scroll { width:100%;overflow-x:auto;-webkit-overflow-scrolling:touch; }
        .arb2-table { width:100%;min-width:880px;border-collapse:collapse;text-align:left; }
        .arb2-table th { padding:14px 15px;background:#0b3b43;color:#a6e7e9;font-size:12px;font-weight:750;white-space:nowrap; }
        .arb2-table td { padding:13px 15px;border-top:1px solid rgba(255,255,255,.07);font-size:14px;color:#f3fcfc;vertical-align:middle; }
        .arb2-table tbody tr { transition:background .2s; }
        .arb2-table tbody tr:nth-child(even) { background:rgba(255,255,255,.018); }
        .arb2-table tbody tr:hover { background:rgba(22,184,199,.09); }
        .arb2-identity { display:flex;align-items:center;gap:9px;white-space:nowrap;font-weight:700; }
        .arb2-logo { width:29px;height:29px;object-fit:contain;border-radius:50%;background:#fff;padding:3px; }
        .arb2-amount { font-weight:800;white-space:nowrap; }
        .arb2-type { display:inline-block;min-width:59px;text-align:center;padding:6px 9px;border-radius:8px;font-size:12px;font-weight:800; }
        .arb2-buy { background:rgba(48,205,147,.12);color:#50e1ac; }
        .arb2-sell { background:rgba(255,112,112,.12);color:#ff9696; }
        .arb2-hash { display:block;max-width:210px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-family:ui-monospace,SFMono-Regular,monospace;color:#b2d1d4;font-size:12px; }
        .arb2-date { color:#a9c4c7;white-space:nowrap;font-size:12px; }
        .arb2-empty { text-align:center;color:#b1c9ca;padding:38px 12px !important; }
        .arb2-footer { padding:12px 18px;color:#8eafb3;font-size:12px;border-top:1px solid var(--line); }
        @media(max-width:700px){.arb2{padding:10px 4px 22px}.arb2-top{padding:15px;border-radius:14px}.arb2-metrics{grid-template-columns:repeat(2,minmax(0,1fr));gap:8px}.arb2-metric{padding:13px}.arb2-metric:last-child{grid-column:1/-1}.arb2-panel{border-radius:14px}.arb2-panelhead{padding:13px}.arb2-actions{width:100%;justify-content:space-between}}
        @media(prefers-reduced-motion:reduce){.arb2 *, .arb2 *::before,.arb2 *::after{transition:none!important;animation:none!important}}
      `}</style>
      <main className="arb2">
        <div className="arb2-inner">
          <header className="arb2-top">
            <div>
              <p className="arb2-eyebrow">RM CROWN MARKET / Trading Activity</p>
              <h1>ARB Trade Live Feed</h1>
              <p className="arb2-sub">Recent arbitrage transaction records from your connected API.</p>
            </div>
            <div className="arb2-actions">
              <span className="arb2-live"><span className={`arb2-dot ${paused || error ? "off" : ""}`} />{paused ? "Paused" : error ? "Connection issue" : lastUpdated ? "Auto-refresh on" : "Connecting"}</span>
              <button type="button" className="arb2-btn" onClick={togglePause}>{paused ? "Resume Updates" : "Pause Updates"}</button>
            </div>
          </header>
          <section className="arb2-metrics" aria-label="Feed summary">
            <div className="arb2-metric"><p className="arb2-metric-label">Total Records</p><p className="arb2-metric-value">{rows.length.toLocaleString()}</p></div>
            <div className="arb2-metric"><p className="arb2-metric-label">Refresh Interval</p><p className="arb2-metric-value">2 seconds</p></div>
            <div className="arb2-metric"><p className="arb2-metric-label">Last Successful Update</p><p className="arb2-metric-value">{lastUpdated ? lastUpdated.toLocaleTimeString() : "—"}</p></div>
          </section>
          <section className="arb2-panel">
            <div className="arb2-panelhead">
              <div><h2>Transaction History</h2><p>Exchange, asset, trade direction, transaction hash and received date</p></div>
              <span className="arb2-count">{rows.length} records</span>
            </div>
            <div className="arb2-scroll">
              <table className="arb2-table">
                <thead><tr><th>SL</th><th>Exchange</th><th>Amount</th><th>Type</th><th>Hash</th><th>Received</th></tr></thead>
                <tbody>
                  {rows.length ? rows.map((row, index) => {
                    const type = String(row.trade_type || "").toLowerCase();
                    return <tr key={row.id ?? `${row.hash || "trade"}-${index}`}>
                      <td>{index + 1}</td>
                      <td><span className="arb2-identity">{row.exchange_logo ? <img className="arb2-logo" src={assetUrl(row.exchange_logo)} alt="" loading="lazy" /> : null}{row.exchanges || "—"}</span></td>
                      <td><span className="arb2-identity">{row.coin_logo ? <img className="arb2-logo" src={assetUrl(row.coin_logo)} alt="" loading="lazy" /> : null}<span className="arb2-amount">{row.btc_amount ?? "—"} {row.coin || ""}</span></span></td>
                      <td><span className={`arb2-type ${type === "buy" ? "arb2-buy" : "arb2-sell"}`}>{String(row.trade_type || "—").toUpperCase()}</span></td>
                      <td><span className="arb2-hash" title={String(row.hash || "")}>{row.hash || "—"}</span></td>
                      <td><span className="arb2-date">{row.created_at || "—"}</span></td>
                    </tr>;
                  }) : <tr><td colSpan={6} className="arb2-empty">{error ? "Unable to update transactions. Retrying automatically." : "No transaction records available yet."}</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="arb2-footer">Updates automatically every 2 seconds while this tab is visible. Data is supplied by the connected API.</div>
          </section>
        </div>
      </main>
    </>
  );
}
